import {
  chooseService,
  type ServiceConfiguration,
  type ServiceSelections,
  type ServiceQuote,
} from "../../shared/proposal-services";
const money = (value: string) =>
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
export function ServiceChoices({
  configuration,
  selections,
  onChange,
  quote,
}: {
  configuration: ServiceConfiguration;
  selections: ServiceSelections;
  onChange: (value: ServiceSelections) => void;
  quote?: ServiceQuote;
}) {
  return (
    <div className="px-services">
      {configuration.services.map((service) => (
        <section
          className="px-panel"
          key={service.key}
          style={{ marginTop: 24 }}
        >
          <p className="px-eyebrow">
            {service.optional ? "Optional service" : "Included service"}
            {service.exclusive_group ? " · Choose one scope in this group" : ""}
          </p>
          <h3>{service.name}</h3>
          {service.location && <p>{service.location}</p>}
          <dl>
            {service.specifications
              .filter((s) => s.value)
              .map((s, i) => (
                <div key={i}>
                  <dt>{s.label}</dt>
                  <dd>{s.value}</dd>
                </div>
              ))}
          </dl>
          <div
            role="radiogroup"
            aria-label={`Choose scope for ${service.name}`}
          >
            {(service.optional || service.exclusive_group) && (
              <label className="px-pick">
                <input
                  type="radio"
                  name={service.key}
                  checked={!selections[service.key]?.option}
                  onChange={() =>
                    onChange(
                      chooseService(
                        configuration,
                        selections,
                        service.key,
                        null,
                      ),
                    )
                  }
                />
                Do not include this service
              </label>
            )}
            {service.options.map((option) => {
              const selected = selections[service.key]?.option === option.key;
              const price = quote?.options?.[`${service.key}:${option.key}`];
              return (
                <div
                  className="px-panel"
                  key={option.key}
                  style={{ marginTop: 12 }}
                >
                  <label className="px-pick">
                    {(service.options.length > 1 ||
                      service.optional ||
                      service.exclusive_group) && (
                      <input
                        type="radio"
                        name={service.key}
                        checked={selected}
                        onChange={() =>
                          onChange(
                            chooseService(
                              configuration,
                              selections,
                              service.key,
                              option.key,
                            ),
                          )
                        }
                      />
                    )}
                    <strong>{option.name}</strong>
                    {price && <b>{money(price.total)}</b>}
                  </label>
                  {configuration.discount &&
                    Number(configuration.discount.value) > 0 && (
                      <small>
                        Before the project discount shown in your final total.
                      </small>
                    )}
                  <p>{option.description}</p>
                  {option.lines.map((line) => (
                    <div
                      key={line.key}
                      style={{
                        paddingBlock: 12,
                        borderTop: "1px solid var(--border)",
                      }}
                    >
                      {line.optional ? (
                        <label>
                          <input
                            type="checkbox"
                            disabled={!selected}
                            checked={
                              selected &&
                              !!selections[service.key]?.lines.includes(
                                line.key,
                              )
                            }
                            onChange={(e) => {
                              const previous = selections[service.key];
                              onChange({
                                ...selections,
                                [service.key]: {
                                  ...previous,
                                  lines: e.target.checked
                                    ? [...previous.lines, line.key]
                                    : previous.lines.filter(
                                        (key) => key !== line.key,
                                      ),
                                },
                              });
                            }}
                          />{" "}
                          {line.name} · Optional
                        </label>
                      ) : (
                        <strong>
                          {line.name}
                          {line.is_fee ? " · Required charge" : ""}
                        </strong>
                      )}
                      <p>
                        {line.quantity} {line.unit} ×{" "}
                        {line.unit_price === null
                          ? "Needs pricing"
                          : money(line.unit_price)}
                      </p>
                      {line.quantity_explanation && (
                        <p>{line.quantity_explanation}</p>
                      )}
                      {line.description && (
                        <p className="px-pre">{line.description}</p>
                      )}
                      {line.discount && Number(line.discount.value) > 0 && (
                        <p>
                          Discount:{" "}
                          {line.discount.type === "percent"
                            ? `${line.discount.value}%`
                            : money(line.discount.value)}
                        </p>
                      )}
                      {[line.manufacturer, line.product_name, line.sku].filter(
                        Boolean,
                      ).length > 0 && (
                        <p>
                          {[line.manufacturer, line.product_name, line.sku]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                      {line.specifications
                        .filter((s) => s.value)
                        .map((s, i) => (
                          <p key={i}>
                            <small>
                              {s.label}: {s.value}
                            </small>
                          </p>
                        ))}
                      {line.requires_order_verification && (
                        <small>
                          Final product and dimensions will be verified before
                          ordering.
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {service.terms && (
            <>
              <h4>Terms & exclusions</h4>
              <p className="px-pre">{service.terms}</p>
            </>
          )}
          {service.warranty && (
            <>
              <h4>Warranty</h4>
              <p className="px-pre">{service.warranty}</p>
            </>
          )}
        </section>
      ))}
    </div>
  );
}
export function ServicePriceSummary({ quote }: { quote: ServiceQuote }) {
  return (
    <>
      {quote.services.map((service) => (
        <div key={service.key}>
          <span>
            {service.name}
            {service.name !== service.option.name &&
            !service.name.endsWith(` · ${service.option.name}`)
              ? ` · ${service.option.name}`
              : ""}
          </span>
          <b>{money(service.total)}</b>
        </div>
      ))}
      {quote.lines
        ?.filter(
          (line) =>
            line.service_key === "project" || line.service_key === "roofing",
        )
        .map((line) => (
          <div key={line.key}>
            <span>
              {line.name}
              {line.service_key === "project"
                ? " · Required project charge"
                : ""}
            </span>
            <b>{money(line.total)}</b>
          </div>
        ))}
      <div>
        <span>Price before discounts and tax</span>
        <b>{money(quote.subtotal)}</b>
      </div>
      {Number(quote.discount) > 0 && (
        <div className="px-discount-row">
          <span>Total savings</span>
          <b>−{money(quote.discount)}</b>
        </div>
      )}
      <div>
        <span>Included tax</span>
        <b>{money(quote.tax)}</b>
      </div>
    </>
  );
}
